import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { SessionContext } from "./SessionContext";
import type { SessionContextValue, SessionState } from "./SessionContext";
import { resolveSession } from "./resolveSession";
import type { SessionDependencies, SessionOutcome } from "./resolveSession";
import { endSession, fetchSeller } from "./seller";
import { signInWasRefused } from "./signIn";

/**
 * Owns sign-in for the whole app.
 *
 * Under canon decision `D-008` there is no credential to spend here, so this provider does
 * one thing: it asks core who is signed in, and it asks again after a sign-out. The effect
 * is still guarded by a ref so React's development double-invoke makes one request rather
 * than two, and only the newest attempt may write state so a slow first read cannot
 * overwrite a retry.
 */

export interface SessionProviderProps {
  children: ReactNode;
  /** Test seam: the real app reads the browser's own address bar. */
  readSignInRefusal?: (() => boolean) | undefined;
  loadSeller?: SessionDependencies["fetchSeller"] | undefined;
  /**
   * Test seam: the real app posts to `POST /v1/seller/session/logout`.
   *
   * Resolves `false` only when the request could not be delivered — see {@link endSession}.
   */
  closeSession?: (() => Promise<boolean>) | undefined;
}

function defaultReadSignInRefusal(): boolean {
  return signInWasRefused(window.location.search);
}

export function SessionProvider({
  children,
  readSignInRefusal = defaultReadSignInRefusal,
  loadSeller = fetchSeller,
  closeSession = endSession,
}: SessionProviderProps) {
  const [state, setState] = useState<SessionState>({ status: "checking" });
  /*
   * A sign-out that never reached core left the founder signed in. That is a different fact
   * from "Dealbid is unreachable", and it is the one worth saying out loud: the desk is
   * still open and the cookie is still live. Held beside the session rather than inside it
   * because it does not change who is signed in — only what they need to be told.
   */
  const [signOutFailed, setSignOutFailed] = useState(false);

  const attemptRef = useRef(0);
  const startedRef = useRef(false);

  const read = useCallback(
    (signInRefused: boolean): Promise<void> => {
      attemptRef.current += 1;
      const attempt = attemptRef.current;
      const isCurrent = () => attempt === attemptRef.current;

      setState({ status: "checking" });

      return resolveSession({ signInRefused, fetchSeller: loadSeller }).then(
        (outcome: SessionOutcome) => {
          if (isCurrent()) setState(outcome);
        },
        () => {
          // resolveSession classifies its own failures; this is the belt-and-braces path
          // for a dependency that throws in a way it cannot.
          if (isCurrent()) setState({ status: "error", reason: "unexpected" });
        },
      );
    },
    [loadSeller],
  );

  const retry = useCallback(() => {
    setSignOutFailed(false);
    void read(readSignInRefusal());
  }, [read, readSignInRefusal]);

  /*
   * A founder who has just signed out did not fail a sign-in, whatever the address bar
   * still says, so the refusal notice is not re-read here — they should see the ordinary
   * invitation to sign in again.
   */
  const signOut = useCallback(async () => {
    const delivered = await closeSession();
    // Read first, then record the failure: `read` decides whether the founder is still
    // signed in, and the notice is only meaningful once that is settled.
    await read(false);
    setSignOutFailed(!delivered);
  }, [closeSession, read]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    retry();
  }, [retry]);

  const value = useMemo<SessionContextValue>(
    () => ({ state, retry, signOut, signOutFailed }),
    [state, retry, signOut, signOutFailed],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
